import NewRelicBrowserReporter, { NewRelicBrowserReporterProps } from "./NewRelicBrowserReporter";
import PerformanceAPIReporter from "./PerformanceAPIReporter";
import Reporter from "./Reporter";

const PARSE_MARK_NAME = /(\w+)(?!:\w+)\.(\w+)(?:-([\w\-()]+))?(?:@(\w+))?/;

/**
 * Creates a metric used to monitor duration of some action
 * Uses browser native performance API's
 */
export default class PerformanceMonitor {

    prefix: string;

    /**
     * @type {Map}
     */
    static metrics = new Map();

    /**
     * @param {String} [prefix="*"]
     */
    constructor(prefix = "Sparkle") {
        this.prefix = prefix;
    }

    createMetric(name?: string): Reporter {
        if (!name) {
            throw new Error("Metric name is required");
        }

        const measureName = `${ this.prefix }.${ name }`;
        const [, module, action, containerId, position] = measureName.match(PARSE_MARK_NAME) ?? [];
        const metricOptions: NewRelicBrowserReporterProps = { module, action, containerId, position };

        /**
         * @type {Reporter}
         */
        const reporter = new Reporter();

        reporter.addReporter(new PerformanceAPIReporter(measureName));
        reporter.addReporter(new NewRelicBrowserReporter(metricOptions));

        PerformanceMonitor.metrics.set(measureName, reporter);

        return reporter;
    }

    /**
     * Prints a table of performance metrics in the console
     * @static
     */
    static logPerformanceInfo() {
        const sparkleMeasures = performance.getEntriesByType("measure")
            .filter((entry) => /^Sparkle./.test(entry.name))
            .map(({ name, duration }) => {
                const measure = PerformanceMonitor.metrics.get(name);
                const [, module, action, containerId, position] = name.match(PARSE_MARK_NAME) ?? [];
                const { advertiserId, lineItemId, creativeId, isEmpty, startTime } = measure.meta || {};

                return ({
                    module,
                    action,
                    containerId,
                    position,
                    startTime: Math.round(startTime),
                    duration: Math.round(duration),
                    advertiserId, lineItemId, creativeId, isEmpty
                });
            });

        console.table(sparkleMeasures);
    }

    /**
     * Prints a table of individual performance metrics in the console
     */
    static logIndividualAdPerformance(pattern?: string) {
        if (!pattern) {
            console.error("Missing required Ad position name");

            return;
        }

        const sparkleMeasures = performance.getEntriesByType("measure")
            .filter((entry) => /^Sparkle./.test(entry.name) && entry.name.lastIndexOf(`-${ pattern }`) !== -1)
            .map(({ name, duration }) => {
                const measure = PerformanceMonitor.metrics.get(name);
                const [, module, action, , position] = name.match(PARSE_MARK_NAME) ?? [];
                const { advertiserId, lineItemId, creativeId, isEmpty, startTime } = measure.meta || {};

                return ({
                    module,
                    action,
                    position,
                    startTime: Math.round(startTime),
                    duration: Math.round(duration),
                    advertiserId, lineItemId, creativeId, isEmpty
                });
            });

        console.table(sparkleMeasures);
    }

    /**
     * Prints debug instructions in console
     */
    static printInstructions() {
        const toc = [
            {
                Command: "Sparkle.logPerformanceInfo()",
                Description: "Displays a table of all Ad metrics taken during the rendering session"
            },
            {
                Command: "Sparkle.logIndividualAdPerformance(position)",
                Description: "Displays a table of metrics per Ad based on position name"
            }
        ];

        console.log("%cSparkle debug instructions:", "background-color: beige; padding: 4px; color: red; font-size: 20px; font-weight: bold;");
        console.table(toc);
    }

}

